import ReactDOM from 'react-dom';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

/**
 * 'mouse' - tooltip follows cursor;
 * 'title' - feels like browser's native 'title' attr (small delay, stays at place after moving cursor);
 * 'titleEllipsed' - the same as above but only triggers when content is overflowing (e.g. text ellipsis is shown).
 */
export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'mouse' | 'title' | 'titleEllipsed';

type Props = React.PropsWithChildren<{
  position: TooltipPosition;
  /** This prop is for cases when the tooltip's target is a void element (img, input etc) */
  targetId?: string;
  offset?: number;
  /** When true, the tooltip is shown regardless of mouse position. */
  forceVisible?: boolean;
}>;

const Tooltip: React.FC<Props> = ({ targetId, position, offset, children, forceVisible }) => {
  const parentFinderRef = useRef<HTMLSpanElement>(null);
  const [style, setStyle] = useState<React.CSSProperties | null>(null);
  const forceVisibleRef = useRef(forceVisible);
  forceVisibleRef.current = forceVisible;

  useLayoutEffect(() => {
    const target = targetId ? document.getElementById(targetId) : parentFinderRef.current?.parentElement;
    if (!target) {
      return;
    }

    const cancelTooltip = createTooltipPositioner({
      target,
      position: position === 'title' || position === 'titleEllipsed' ? 'mouse' : position,
      onShow: (s) => {
        if (!forceVisibleRef.current) setStyle(s);
      },
      onHide: () => {
        if (!forceVisibleRef.current) setStyle(null);
      },
      delay: position === 'title' || position === 'titleEllipsed' ? 500 : 0,
      offset: offset ?? 0,
      visibleOnlyIfOverflows: position === 'titleEllipsed',
    });
    return () => cancelTooltip();
  }, [targetId, position, offset]);

  useEffect(() => {
    if (forceVisible === undefined) return;
    if (!forceVisible) {
      setStyle(null);
      return;
    }
    const target = targetId ? document.getElementById(targetId) : parentFinderRef.current?.parentElement;
    if (!target) return;
    const resolvedOffset = offset ?? 0;
    const resolvedPosition = position === 'title' || position === 'titleEllipsed' ? 'bottom' : position;

    const computeStyle = (): React.CSSProperties => {
      const bbox = target.getBoundingClientRect();
      switch (resolvedPosition) {
        case 'top':
          return { left: (bbox.left + bbox.right) / 2, top: bbox.top - resolvedOffset, transform: 'translate(-50%, -100%)' };
        case 'bottom':
          return { left: (bbox.left + bbox.right) / 2, top: bbox.bottom + resolvedOffset, transform: 'translateX(-50%)' };
        case 'left':
          return { left: bbox.left - resolvedOffset, top: (bbox.top + bbox.bottom) / 2, transform: 'translate(-100%, -50%)' };
        case 'right':
          return { left: bbox.right + resolvedOffset, top: (bbox.top + bbox.bottom) / 2, transform: 'translateY(-50%)' };
        case 'mouse':
        default:
          return { left: (bbox.left + bbox.right) / 2, top: bbox.bottom + resolvedOffset, transform: 'translateX(-50%)' };
      }
    };

    setStyle(computeStyle());

    const handleScroll = () => setStyle(computeStyle());
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [forceVisible, targetId, position, offset]);

  const markerNode: React.ReactNode = !targetId && <span ref={parentFinderRef} style={{ display: 'none' }} aria-hidden />;
  const tooltipNode =
    style &&
    ReactDOM.createPortal(
      <TooltipBox role="tooltip" style={style}>
        {children}
      </TooltipBox>,
      document.body
    );

  return (
    <>
      {markerNode}
      {tooltipNode}
    </>
  );
};

export default Tooltip;

const TooltipBox = styled.div`
  position: fixed;
  max-width: 35rem;
  z-index: 9999999;
  padding: 6px 8px;
  border-radius: 2px;
  background: #5b5b5b;
  font-family: Inter;
  font-weight: 500;
  color: #ffffff;
  font-size: 12px;
  line-height: 1.25;
  pointer-events: none;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
`;

interface TooltipParameters {
  target: HTMLElement;
  position: 'top' | 'bottom' | 'left' | 'right' | 'mouse';
  onShow(style: React.CSSProperties): void;
  onHide(): void;
  delay: number;
  offset: number;
  visibleOnlyIfOverflows: boolean;
}

function createTooltipPositioner({
  target,
  position,
  onShow,
  onHide,
  delay,
  offset,
  visibleOnlyIfOverflows,
}: TooltipParameters) {
  const abortController = new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined = undefined;
  let latestMousePosition: { x: number; y: number } | null = null;
  let isVisible = false;

  const hideTooltip = () => {
    isVisible = false;
    clearTimeout(timeout);
    timeout = undefined;
    latestMousePosition = null;
    window.removeEventListener('scroll', hideTooltip);
    onHide();
  };

  const handleTimerEnd = () => {
    timeout = undefined;

    if (visibleOnlyIfOverflows) {
      const isOverflowing = target.scrollWidth > target.clientWidth;
      if (!isOverflowing) {
        return;
      }
    }

    isVisible = true;

    if (position === 'mouse') {
      if (!latestMousePosition) {
        return;
      }
      const tooltipWidthToStillLookGood = 200;
      const windowWidthWithoutScrollbar = Math.min(document.body.scrollWidth, window.innerWidth);
      const isTooCloseToRightEdge = latestMousePosition.x >= windowWidthWithoutScrollbar - tooltipWidthToStillLookGood;
      const cursorOffsetX = 12;
      const cursorOffsetY = 16;
      onShow(
        !isTooCloseToRightEdge
          ? {
              left: latestMousePosition.x + cursorOffsetX,
              top: latestMousePosition.y + cursorOffsetY,
            }
          : {
              right: windowWidthWithoutScrollbar - latestMousePosition.x, // No offset because cursor icons are usually drawn to the right and don't overlap this way
              top: latestMousePosition.y + cursorOffsetY,
            }
      );
      return;
    }

    const bbox = target.getBoundingClientRect();
    switch (position) {
      case 'top':
        onShow({
          left: (bbox.left + bbox.right) / 2,
          top: bbox.top - offset,
          transform: 'translate(-50%, -100%)',
        });
        break;

      case 'bottom':
        onShow({
          left: (bbox.left + bbox.right) / 2,
          top: bbox.bottom + offset,
          transform: 'translateX(-50%)',
        });
        break;

      case 'left':
        onShow({
          left: bbox.left - offset,
          top: (bbox.top + bbox.bottom) / 2,
          transform: 'translate(-100%, -50%)',
        });
        break;

      case 'right':
        onShow({
          left: bbox.right + offset,
          top: (bbox.top + bbox.bottom) / 2,
          transform: 'translateY(-50%)',
        });
        break;
    }
  };

  target.addEventListener(
    'mouseover',
    (ev) => {
      latestMousePosition = { x: ev.clientX, y: ev.clientY };
      timeout = setTimeout(handleTimerEnd, delay);
      window.addEventListener('scroll', hideTooltip, {
        passive: true,
        signal: abortController.signal,
      });
    },
    { passive: true, signal: abortController.signal }
  );

  if (position === 'mouse') {
    target.addEventListener(
      'mousemove',
      (ev) => {
        if (!isVisible || !delay) {
          latestMousePosition = { x: ev.clientX, y: ev.clientY };
          if (!timeout) {
            timeout = setTimeout(handleTimerEnd, delay);
          }
        }
      },
      { passive: true, signal: abortController.signal }
    );
  }

  target.addEventListener('mouseout', hideTooltip, {
    passive: true,
    signal: abortController.signal,
  });

  return () => {
    abortController.abort();
    clearTimeout(timeout);
    onHide();
  };
}
